<template>
    <mode-switcher
        ref="modeSwitcher"
        :main-mode-text="priceListCodes && priceListCodes.length > 1 ? '' : $t('create')"
        :create-mode-text="$t('price_list_creating')"
        :edit-mode-text="$t('price_list_editing')"
        @reset="priceListEditModalSelected = null"
    >
        <template v-slot:btn-block>
            <div class="full-width d-flex align-center">
                <PriceTypeModal
                    v-if="priceListCodes && priceListCodes.length > 1"
                    ref="priceTypeModal"
                    v-model="priceCode"
                    :price-list-codes="priceListCodes"
                    @continue="selectPriceList"
                />
                <v-spacer />
                <FilterStatusToggle v-model="activeFilter" />
            </div>
        </template>
        <template v-slot:main-mode>
            <v-data-table
                :items="priceListsFiltered"
                :headers="headers"
                :loading="isPriceListsLoading"
                class="elevation-1 table-server"
                :sort-by="[{key: 'name', order: 'asc'}]"
                must-sort
            >
                <template v-slot:item.name="{item}">
                    <span class="text-link font-weight-medium text-decoration-underline" @click="showEditModal(item)">
                        {{ item.name }}
                    </span>
                </template>
                <template v-slot:item.priceTypeCode="{item}">
                    <span>
                        {{ $t(`priceListCode.${item.priceTypeCode}`) }}
                    </span>
                </template>
                <template v-slot:item.dates="{item}">
                    <span>
                        {{ dateRange(item.tariffLinks) }}
                    </span>
                </template>
                <template v-slot:item.active="{item}">
                    <span>
                        {{ item.active ? $t('active') : $t('inactive') }}
                    </span>
                </template>
                <template v-slot:item.actions="{item}">
                    <span>
                        <v-btn variant="text" size="small" color="primary" @click="copyPriceList(item)">
                            {{ $t('copy') }}
                        </v-btn>
                    </span>
                </template>
            </v-data-table>
        </template>
        <template v-slot:additional-mode>
            <component
                :is="productPricesPriceListsComponent"
                :price-list="priceListEditModalSelected"
                :contract="contract"
                :selected-locale="selectedLocale"
                :price-code="priceCode"
                :price-list-codes="priceListCodes"
                :price-list-calculation-types="priceListCalculationTypes"
                @on-close="onCloseEditModal"
                @on-save="onPriceListSave"
            />
        </template>
    </mode-switcher>
</template>

<script lang="ts">
    import {Component, Emit, Prop, Vue, Provide} from 'vue-facing-decorator'
    import {clone} from '~/utils/helpers'
    import PriceTypeModal from '~/components/supplier/prices/modals/PriceTypeModal.vue'

    @Component({
        components: {
            PriceTypeModal,
            ProductPricesPriceListsModal: defineAsyncComponent(
                () => import('~/components/supplier/prices/modals/ProductPricesPriceListsModal.vue')
            ),
            ProductPricesPriceListsHotelModal: defineAsyncComponent(
                () => import('~/components/supplier/prices/modals/ProductPricesPriceListsHotelModal.vue')
            ),
        },
    })
    export class ProductPricesPriceLists extends Vue {
        @Prop() contract
        @Prop() selectedLocale
        @Prop() priceLists
        @Prop() isPriceListsLoading

        priceListEditModalSelected = null
        activeFilter = true
        priceCode = null
        priceListCodes = []
        @Provide
        priceListCalculationTypes = []

        get paxes() {
            return supplierStore.paxes
        }

        async mounted() {
            await this.loadPriceListCodes()
            await this.loadPriceListCalculationTypes()
        }

        selectPriceList() {
            this.$refs.modeSwitcher.create()
        }

        async loadPriceListCodes() {
            try {
                this.priceListCodes = await this.$api.priceListCodes.get(this.contract.serviceType)
            } catch (e) {
                this.$toast.error(this.$t('error_message.load_error'))
            }
        }

        async loadPriceListCalculationTypes() {
            try {
                this.priceListCalculationTypes = await this.$api.priceListCalculationTypes.get(
                    this.contract.serviceType
                )
            } catch (e) {
                this.$toast.error(this.$t('error_message.load_error'))
            }
        }

        @Emit()
        async onPriceListSave() {}

        showEditModal(priceList) {
            this.priceListEditModalSelected = priceList
            this.$refs.modeSwitcher.edit()
        }

        copyPriceList(priceList) {
            const priceListCopied = clone(priceList)

            delete priceListCopied.id

            priceListCopied.tariffLinks.forEach(tl => {
                delete tl.linkId
            })

            priceListCopied.prices.forEach(price => {
                delete price.id
            })

            this.showEditModal(priceListCopied)
        }

        onCloseEditModal() {
            this.$refs.modeSwitcher.goToTable()
        }

        get headers() {
            return [
                {title: this.$t('name'), value: 'name', sortable: true},
                {title: this.$t('type_of_price_list'), value: 'priceTypeCode'},
                {title: this.$t('dates'), value: 'dates'},
                {title: this.$t('status'), value: 'active'},
                {title: this.$t('actions'), value: 'actions'},
            ]
        }

        get priceListsFiltered() {
            return this.priceLists.filter(el => this.activeFilter === el.active)
        }

        get productPricesPriceListsComponent() {
            return this.contract.serviceType === 'ownHotel'
                ? 'ProductPricesPriceListsHotelModal'
                : 'ProductPricesPriceListsModal'
        }

        dateRange(tariffs) {
            if (!tariffs.length) return ''
            let earliestDateFrom = tariffs[0].dateFrom
            let latestDateTo = tariffs[0].dateTo

            tariffs.forEach(tariff => {
                if (new Date(tariff.dateFrom) < new Date(earliestDateFrom)) {
                    earliestDateFrom = tariff.dateFrom
                }
                if (new Date(tariff.dateTo) > new Date(latestDateTo)) {
                    latestDateTo = tariff.dateTo
                }
            })

            return `${earliestDateFrom && this.$filters.dateShortFormat(earliestDateFrom)} - ${latestDateTo && this.$filters.dateShortFormat(latestDateTo)}`
        }
    }

    export default ProductPricesPriceLists
</script>
